import { BASE_URL } from './config';
import { formatValidationDetail } from './utils';

// ─────────────────────────────────────────────
// TOKEN MANAGEMENT
// ─────────────────────────────────────────────
let _token = null;

export const setToken = (t) => { _token = t; };
export const getToken = () => _token;

// ─────────────────────────────────────────────
// IMPERSONATION
// A super admin signing in AS another user. The admin's own token pair is
// parked under separate keys so exiting is a local restore — no second
// sign-in, and nothing to go wrong if the network is down when they leave.
// ─────────────────────────────────────────────
const ADMIN_TOKEN_KEY = 'pf_admin_token';
const ADMIN_REFRESH_KEY = 'pf_admin_refresh';

export const isImpersonating = () => !!localStorage.getItem(ADMIN_TOKEN_KEY);

// Park the administrator's session. Deliberately refuses to overwrite an
// existing stash: impersonating from inside an impersonation would otherwise
// bury the real admin token and strand them in the second account.
export const stashAdminSession = () => {
  if (localStorage.getItem(ADMIN_TOKEN_KEY)) return false;
  const t = localStorage.getItem('pf_token');
  const r = localStorage.getItem('pf_refresh');
  if (!t) return false;
  localStorage.setItem(ADMIN_TOKEN_KEY, t);
  localStorage.setItem(ADMIN_REFRESH_KEY, r || '');
  return true;
};

export const restoreAdminSession = () => {
  const t = localStorage.getItem(ADMIN_TOKEN_KEY);
  if (!t) return null;
  const r = localStorage.getItem(ADMIN_REFRESH_KEY) || '';
  localStorage.setItem('pf_token', t);
  localStorage.setItem('pf_refresh', r);
  localStorage.removeItem(ADMIN_TOKEN_KEY);
  localStorage.removeItem(ADMIN_REFRESH_KEY);
  setToken(t);
  return { access_token: t, refresh_token: r };
};

export const clearAdminSession = () => {
  localStorage.removeItem(ADMIN_TOKEN_KEY);
  localStorage.removeItem(ADMIN_REFRESH_KEY);
};

// ─────────────────────────────────────────────
// BASE REQUEST HELPER
// ─────────────────────────────────────────────
const CACHE_PREFIX = 'pf_cache_';
const activeRequests = new Map();
const responseCache = new Map();

export const clearApiCache = () => {
  responseCache.clear();
};

let isRefreshing = false;
let refreshPromise = null;

// fetch has NO timeout. Lose the network mid-request — a dropped Wi-Fi, a
// laptop lid closing, a server that accepts the connection and never answers —
// and the promise simply stays pending. It never rejects, so callers' catch
// blocks never run and their `finally` never resets the button: the UI sits on
// "Submitting…" forever with no way out.
//
// Every request goes through here so that cannot happen. On timeout the abort
// surfaces as a normal rejection and the caller's existing error handling
// takes over.
// 2 minutes. Raised from 60s: Render cold-starts and a loaded VPS can both
// take well over a minute to answer a first request, and cutting off a request
// that is still working is worse than waiting for it.
//
// A two-minute wait with no feedback is its own failure though — people assume
// the app has hung and start clicking. So the wait is split: at SLOW_REQUEST_MS
// the request announces that it is taking a while and is still running, and
// only at REQUEST_TIMEOUT_MS is it actually given up on.
const REQUEST_TIMEOUT_MS = 120000;
export const SLOW_REQUEST_MS = 60000;

// Where the slow-request warning is announced. A plain DOM event rather than a
// callback or an import: api.js is not a component and must not reach into the
// toast system, and this way anything that cares can listen without api.js
// knowing it exists.
export const SLOW_REQUEST_EVENT = 'api:slow-request';

// Neither of these failures produces an HTTP response, so there is no `detail`
// from the server to show. Everything useful therefore has to be attached
// here: which call failed, how long we waited, whether it could have changed
// anything, and which host we could not reach. errorText turns that into the
// sentence the user sees.
const describeFailure = (e, { method, path, timeout, host }) => {
  const err = new Error('');
  err.method = method;
  err.path = path;
  err.host = host;
  err.timeoutMs = timeout;
  // A GET can always be retried safely. Anything else may have been received
  // and acted on before the connection died, so the user must be told to
  // check rather than blindly resubmit.
  err.mutating = method !== 'GET';
  // These strings are user-facing. Plenty of callers show err.message straight
  // in a toast rather than routing it through errorText, so this is not an
  // internal label — it is what somebody reads when the call fails.
  if (e?.name === 'AbortError') {
    err.kind = 'timeout';
    err.message = 'Your request took too long to respond and timed out';
  } else {
    err.kind = 'unreachable';
    err.message = 'Poor connection, try again';
  }
  err.cause = e;
  return err;
};

const hostOf = (url) => { try { return new URL(url).host; } catch { return ''; } };

const fetchWithTimeout = async (url, options = {}, timeout = REQUEST_TIMEOUT_MS, ctx = {}) => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeout);

  // Fired once, at the halfway mark, only if the request is still in flight.
  // Carries `mutating` so the listener can say the right thing: a slow GET is
  // an inconvenience, a slow POST is something the reader must not respond to
  // by pressing the button again.
  const method = options.method || 'GET';
  const slowTimer = setTimeout(() => {
    try {
      window.dispatchEvent(new CustomEvent(SLOW_REQUEST_EVENT, {
        detail: {
          method,
          path: ctx.path || url,
          waitedMs: SLOW_REQUEST_MS,
          timeoutMs: timeout,
          mutating: method !== 'GET',
        },
      }));
    } catch { /* a warning that cannot be shown must not fail the request */ }
  }, Math.min(SLOW_REQUEST_MS, timeout));

  try {
    return await fetch(url, { ...options, signal: controller.signal });
  } catch (e) {
    throw describeFailure(e, {
      method, path: ctx.path || url,
      timeout, host: hostOf(url),
    });
  } finally {
    clearTimeout(timer);
    clearTimeout(slowTimer);
  }
};

const _doReq = async (method, path, body, isForm) => {
  console.log(`[API REQUEST] ${method} ${path}`);
  const headers = {};
  if (_token) headers['Authorization'] = `Bearer ${_token}`;
  if (body && !isForm) headers['Content-Type'] = 'application/json';

  let res = await fetchWithTimeout(`${BASE_URL}${path}`, {
    method,
    headers,
    body: isForm ? body : body ? JSON.stringify(body) : undefined,
  }, REQUEST_TIMEOUT_MS, { path });

  if (!res.ok) {
    if (res.status === 401 && path !== '/auth/login' && path !== '/auth/refresh') {
      const storedRefresh = localStorage.getItem('pf_refresh');
      if (storedRefresh) {
        if (!isRefreshing) {
          isRefreshing = true;
          refreshPromise = fetchWithTimeout(`${BASE_URL}/auth/refresh`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ refresh_token: storedRefresh })
          }, REQUEST_TIMEOUT_MS, { path: '/auth/refresh' }).then(async r => {
            if (!r.ok) throw new Error('Refresh failed');
            const data = await r.json();
            const newAccess = data.access_token || data.token;
            setToken(newAccess);
            localStorage.setItem('pf_token', newAccess);
            if (data.refresh_token) localStorage.setItem('pf_refresh', data.refresh_token);
            return newAccess;
          }).finally(() => {
            isRefreshing = false;
          });
        }

        try {
          const newToken = await refreshPromise;
          headers['Authorization'] = `Bearer ${newToken}`;
          const retryRes = await fetchWithTimeout(`${BASE_URL}${path}`, {
            method,
            headers,
            body: isForm ? body : body ? JSON.stringify(body) : undefined,
          }, REQUEST_TIMEOUT_MS, { path });
          if (retryRes.ok) {
            return retryRes.json().catch(() => ({}));
          }
        } catch (e) {
          // If refresh fails, fall through to normal 401 handling
        }
      }

      setToken(null);
      localStorage.removeItem('pf_token');
      localStorage.removeItem('pf_refresh');
      window.dispatchEvent(new Event('unauthorized'));
      clearApiCache();
    }
    // A non-JSON body is normal here: a 502 from a restarting gateway answers
    // with HTML, and res.json() rejects on it.
    const err = await res.json().catch(() => ({
      detail: `Request failed (HTTP ${res.status} ${res.statusText || ''})`.trim(),
    }));
    // A 422's detail is an ARRAY of per-field problems. Passing it straight to
    // Error() stringifies it to "[object Object]" and loses the one thing the
    // server took the trouble to say. Format it here too, not only in
    // errorText, so anything reading err.message directly still gets the
    // actual reason.
    const message =
      formatValidationDetail(err.detail, err.errors) ||
      (typeof err.detail === 'string' ? err.detail : '') ||
      (err.detail && (err.detail.msg || err.detail.message)) ||
      JSON.stringify(err);
    const errorObj = new Error(message);
    errorObj.data = err;
    errorObj.status = res.status;
    throw errorObj;
  }
  const parsed = await res.json().catch(() => ({}));

  // The paged list endpoints report how many rows exist in total via a header.
  // It is attached to the array rather than wrapping the response, because
  // listMyPRs is also used for draft counts on three other screens and every
  // one of them expects a plain array. Non-enumerable so it never shows up in
  // iteration, spreads or JSON.stringify — only code that asks for it by name
  // sees it at all.
  const total = res.headers.get('X-Total-Count');
  if (total !== null && parsed && typeof parsed === 'object') {
    Object.defineProperty(parsed, 'totalCount', {
      value: Number(total), enumerable: false, configurable: true,
    });
  }
  return parsed;
};

const req = async (method, path, body = null, isForm = false) => {
  const isGet = method === 'GET';
  const cacheKey = `${CACHE_PREFIX}${path}`;

  if (isGet) {
    if (responseCache.has(cacheKey)) {
      return responseCache.get(cacheKey);
    }
    if (activeRequests.has(path)) {
      return activeRequests.get(path);
    }
  }

  const promise = (async () => {
    const data = await _doReq(method, path, body, isForm);
    if (isGet) {
      responseCache.set(cacheKey, data);
    } else {
      clearApiCache();
    }
    return data;
  })();

  if (isGet) {
    activeRequests.set(path, promise);
    promise.catch(() => { }).finally(() => activeRequests.delete(path));
  }

  return promise;
};

const get = (path) => req('GET', path);
// live data (device health, punches as they arrive): skips the response cache, which only a write clears
const fresh = (path) => _doReq('GET', path);
const post = (path, body, f) => req('POST', path, body, f);
const put = (path, body) => req('PUT', path, body);
const patch = (path, body) => req('PATCH', path, body);
// Body is optional — only the hard delete sends one (a typed confirmation).
const del = (path, body = null) => req('DELETE', path, body);

// multipart upload (FormData) with any method — the HR file routes take PUT and POST
const upload = (method, path, formData) => req(method, path, formData, true);

// a private file (photo, scan) as a Blob: it needs the login token, so it cannot be a plain <img src>/link.
// A 401 means the access token expired; a cheap authenticated call refreshes it, then one retry.
const blob = async (path, retried = false) => {
  const res = await fetchWithTimeout(`${BASE_URL}${path}`, { headers: _token ? { Authorization: `Bearer ${_token}` } : {} }, REQUEST_TIMEOUT_MS, { path });
  if (res.status === 401 && !retried) {
    await _doReq('GET', '/users/me').catch(() => null);
    return blob(path, true);
  }
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    const e = new Error(typeof err.detail === 'string' ? err.detail : `Could not open the file (HTTP ${res.status})`);
    e.status = res.status;
    throw e;
  }
  return res.blob();
};

// The signed-in person's own profile photo (their HR employee record), for the avatars.
export const getMyPhoto = () => blob('/users/me/photo');
// Another user's photo, for the admin user pages (administrators only).
export const getUserPhoto = (userId) => blob(`/users/${userId}/photo`);

// The HR & People module builds its own calls (src/hr/api.js) on these.
export const http = { get, fresh, post, put, patch, del, upload, blob };

// ─────────────────────────────────────────────
// AUTH
// ─────────────────────────────────────────────
export const login = (email, password) => {
  const fd = new FormData();
  fd.append('username', email);
  fd.append('password', password);
  return req('POST', '/auth/login', fd, true);
};
export const refreshToken = (token) => post('/auth/refresh', { refresh_token: token });
// Two-step sign-in (authenticator app). Called with the short-lived mfa_token the password step returned.
export const mfaSetup = (mfaToken) => post('/auth/mfa/setup', { mfa_token: mfaToken });
export const mfaSetupConfirm = (mfaToken, code) => post('/auth/mfa/setup/confirm', { mfa_token: mfaToken, code });
export const mfaVerify = (mfaToken, code) => post('/auth/mfa/verify', { mfa_token: mfaToken, code });
// Administrator: forget a user's authenticator (lost phone) — they set it up again at next sign-in.
export const resetUserMfa = (userId) => post(`/users/${userId}/mfa/reset`, {});
// Self-service "forgot password" was withdrawn at the client's request — a
// user who is locked out contacts an administrator, who issues a temporary
// password. The only way to change a password is with the current one.
export const changePassword = (currentPassword, newPassword) =>
  post('/auth/change-password', { current_password: currentPassword, new_password: newPassword });
export const register = (data) => post('/auth/register', data);
export const verifyUser = (token) => post(`/auth/verify?token=${token}`);

// ─────────────────────────────────────────────
// UNIT OF MEASURE
// ─────────────────────────────────────────────
export const getUOMOptions = (activeOnly = true) => get(`/uom?active_only=${activeOnly}`);
export const createUOM     = (data)              => post('/uom', data);
export const updateUOM     = (id, data)          => put(`/uom/${id}`, data);
export const deleteUOM     = (id)                => del(`/uom/${id}`);

// ─────────────────────────────────────────────
// USERS & DEPARTMENTS
// ─────────────────────────────────────────────
export const listDepartments = (skip = 0, limit = 100, activeOnly = false) => get(`/departments/?skip=${skip}&limit=${limit}&active_only=${activeOnly}`);
export const createDepartment = (data) => post('/departments/', data);
export const updateDepartment = (id, data) => put(`/departments/${id}`, data);
export const deleteDepartment = (id) => del(`/departments/${id}`);

export const getMe = () => get('/users/me');

// Super admin only. Returns a token that acts as `userId`; the caller is
// responsible for stashing the admin's own session first.
export const impersonateUser = (userId) => post(`/auth/impersonate/${userId}`, {});
// Closes the audit window. Called with the impersonation token still active,
// so it must run BEFORE the admin's session is restored.
export const stopImpersonation = () => post('/auth/impersonate/stop', {});
export const updateMe = (data) => put('/users/me', data);
export const listUsers = (skip = 0, limit = 50, q = '') => {
  const params = new URLSearchParams({ skip, limit });
  if (q) params.set('q', q);
  return get(`/users/list-users?${params}`);
};
export const getUser = (id) => get(`/users/${id}/`);
export const updateUser = (id, data) => put(`/users/${id}`, data);
export const deleteUser = (id) => del(`/users/${id}`);
// Hard delete — permanent, distinct from deleteUser which only deactivates.
// The preview drives the confirmation dialog; the delete refuses unless the
// admin retypes the exact email.
export const previewUserHardDelete = (id) => get(`/users/${id}/delete-impact`);
export const hardDeleteUser = (id, confirmEmail) =>
  del(`/users/${id}/hard`, { confirm_email: confirmEmail });
export const assignUserRoles = (id, data) => post(`/users/${id}/roles`, data);
export const getUserAccessStats = () => get('/users/access-stats');

// Account security — super_admin only.
export const getUserProfile = (id) => get(`/users/${id}/profile`);
// a user's own grants / revocations on top of their roles
export const getUserPermissions = (id) => get(`/users/${id}/permissions`);
export const saveUserPermissions = (id, data) => put(`/users/${id}/permissions`, data);
export const adminResetPassword = (id, { temp_password, expires_in_hours = 24, unlock = true, force_change = true }) =>
  post(`/users/${id}/reset-password`, { temp_password, expires_in_hours, unlock, force_change });
export const lockUser = (id, reason) => post(`/users/${id}/lock`, { reason });
export const unlockUser = (id) => post(`/users/${id}/unlock`);

// Consideration statuses — the configurable "why is this parked" reasons the
// Cost Control queue groups by. Readable by any signed-in user (the return
// dialog needs the list); only editing is super_admin.
export const listConsiderationStatuses = ({ activeOnly = false, returnReasonsOnly = false } = {}) =>
  get(`/consideration-statuses/?active_only=${activeOnly}&return_reasons_only=${returnReasonsOnly}`);
export const createConsiderationStatus = (data) => post('/consideration-statuses/', data);
export const updateConsiderationStatus = (id, data) => put(`/consideration-statuses/${id}`, data);
export const deleteConsiderationStatus = (id) => del(`/consideration-statuses/${id}`);

// Request Management (Admin) — hard delete of a requisition or voucher with
// its approval flow. Separate from the workflow routes: these bypass it
// entirely, so they are super_admin-only and demand a typed confirmation.
export const getRecordDeleteImpact = (entityType, id) =>
  get(`/request-management/${entityType}/${id}/delete-impact`);
export const hardDeleteRecord = (entityType, id, confirmReference) =>
  del(`/request-management/${entityType}/${id}`, { confirm_reference: confirmReference });

// ─────────────────────────────────────────────
// ROLES
// ─────────────────────────────────────────────
export const listRoles = () => get('/roles/list-roles');
export const createRole = (data) => post('/roles', data);
export const updateRole = (id, data) => put(`/roles/${id}`, data);

// ─────────────────────────────────────────────
// ORG HIERARCHY  (Phase 3)
// ─────────────────────────────────────────────
export const getOrgTree = () => get('/org/tree');
export const listCompanies = () => get('/org/companies');
export const createCompany = (data) => post('/org/companies', data);
export const updateCompany = (id, data) => put(`/org/companies/${id}`, data);
export const deleteCompany = (id) => del(`/org/companies/${id}`);

export const listOrgStates = (companyId) =>
  get(`/org/states${companyId ? `?company_id=${companyId}` : ''}`);
export const createOrgState = (data) => post('/org/states', data);
export const updateOrgState = (id, data) => put(`/org/states/${id}`, data);
export const deleteOrgState = (id) => del(`/org/states/${id}`);

export const listOrgBranches = (stateId) =>
  get(`/org/branches${stateId ? `?state_id=${stateId}` : ''}`);
export const createOrgBranch = (data) => post('/org/branches', data);
export const updateOrgBranch = (id, data) => put(`/org/branches/${id}`, data);
export const deleteOrgBranch = (id) => del(`/org/branches/${id}`);

export const listOrgSites = (branchId) =>
  get(`/org/sites${branchId ? `?branch_id=${branchId}` : ''}`);
export const createOrgSite = (data) => post('/org/sites', data);
export const updateOrgSite = (id, data) => put(`/org/sites/${id}`, data);
export const deleteOrgSite = (id) => del(`/org/sites/${id}`);

// ─────────────────────────────────────────────
// APPROVAL LIMITS  (Phase 3)
// ─────────────────────────────────────────────
export const listApprovalLimits = () => get('/approval-limits/');
export const upsertApprovalLimit = (data) => post('/approval-limits/', data);
export const deleteApprovalLimit = (id) => del(`/approval-limits/${id}`);

// ─────────────────────────────────────────────
// MODIFICATION ACCESS  (Phase 3)
// ─────────────────────────────────────────────
export const listModificationAccess = () => get('/modification-access/');
export const upsertModificationAccess = (data) => post('/modification-access/', data);
export const deleteModificationAccess = (id) => del(`/modification-access/${id}`);

// ─────────────────────────────────────────────
// FISCAL YEARS  (Phase 4)
// ─────────────────────────────────────────────
export const listFiscalYears = () => get('/fiscal-years/');
export const createFiscalYear = (data) => post('/fiscal-years/', data);
export const updateFiscalYear = (id, data) => put(`/fiscal-years/${id}`, data);
export const deleteFiscalYear = (id) => del(`/fiscal-years/${id}`);

export const listUserYearAccess = (params = {}) => {
  const qs = Object.entries(params).filter(([, v]) => v != null).map(([k, v]) => `${k}=${v}`).join('&');
  return get(`/fiscal-years/user-access${qs ? `?${qs}` : ''}`);
};
export const upsertUserYearAccess = (data) => post('/fiscal-years/user-access', data);
export const deleteUserYearAccess = (id) => del(`/fiscal-years/user-access/${id}`);
export const bulkUpsertUserYearAccess = (data) => post('/fiscal-years/user-access/bulk', data);

// ─────────────────────────────────────────────
// ACCESS REQUESTS  (Phase 5)
// ─────────────────────────────────────────────
export const listAccessRequests = (params = {}) => {
  const qs = Object.entries(params).filter(([, v]) => v != null).map(([k, v]) => `${k}=${v}`).join('&');
  return get(`/access-requests/${qs ? `?${qs}` : ''}`);
};
export const createAccessRequest = (data) => post('/access-requests/', data);
export const getAccessRequest = (id) => get(`/access-requests/${id}`);
export const approveAccessRequest = (id, data) => put(`/access-requests/${id}/approve`, data);
export const rejectAccessRequest = (id, data) => put(`/access-requests/${id}/reject`, data);
export const cancelAccessRequest = (id) => put(`/access-requests/${id}/cancel`, {});

// ─────────────────────────────────────────────
// AUDIT TRAIL  (Phase 6)
// ─────────────────────────────────────────────
export const listAuditLog = (params = {}) => {
  const qs = Object.entries(params).filter(([, v]) => v != null && v !== '').map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join('&');
  return get(`/access-audit/${qs ? `?${qs}` : ''}`);
};
export const getAuditEventTypes = () => get('/access-audit/event-types');
export const getAuditStats = () => get('/access-audit/stats');

// ─────────────────────────────────────────────
// ACCESS REPORTS  (Phase 6)
// ─────────────────────────────────────────────
export const getAccessReportSummary = () => get('/access-reports/summary');
export const getUserAccessInventory = () => get('/access-reports/user-access-inventory');

// ─────────────────────────────────────────────
// DISCIPLINES
// ─────────────────────────────────────────────
export const listDisciplines = (skip = 0, limit) =>
  get(`/disciplines?skip=${skip}` + (limit != null ? `&limit=${limit}` : ''));
export const createDiscipline = (data) => post('/disciplines', data);
export const updateDiscipline = (id, data) => put(`/disciplines/${id}`, data);
export const deleteDiscipline = (id) => del(`/disciplines/${id}`);

// ─────────────────────────────────────────────
// PROJECTS
// ─────────────────────────────────────────────
// limit is optional server-side — omit it and every project comes back.
// The old (skip=0, limit=20) default meant callers that passed nothing —
// the CC review and requisition project dropdowns — only ever saw 20.
export const listProjects = (skip = 0, limit) =>
  get(`/projects?skip=${skip}` + (limit != null ? `&limit=${limit}` : ''));
export const getProject = (id) => get(`/projects/${id}`);
export const createProject = (data) => post('/projects', data);
export const updateProject = (id, data) => put(`/projects/${id}`, data);
export const deleteProject = (id) => del(`/projects/${id}`);

// ─────────────────────────────────────────────
// PROJECT_DIVISIONS
// ─────────────────────────────────────────────
export const listProjectDivisions = (pid) => get(`/project-divisions/${pid}/project-divisions`);
export const getProjectDivision = (id) => get(`/project-divisions/${id}`);
export const createProjectDivision = (pid, data) => post(`/project-divisions/${pid}/project-divisions`, data);
export const updateProjectDivision = (id, data) => put(`/project-divisions/${id}`, data);
export const deleteProjectDivision = (id) => del(`/project-divisions/${id}`);

// ─────────────────────────────────────────────
// MILESTONES
// ─────────────────────────────────────────────
export const listProjectMilestones = (pid) => get(`/milestones/projects/${pid}/milestones`);
export const listProjectDivisionMilestones = (bid) => get(`/milestones/project_divisions/${bid}/milestones`);
export const createProjectMilestone = (pid, data) => post(`/milestones/projects/${pid}/milestones`, data);
export const createProjectDivisionMilestone = (bid, data) => post(`/milestones/project_divisions/${bid}/milestones`, data);
export const updateMilestone = (id, data) => put(`/milestones/${id}`, data);
export const deleteMilestone = (id) => del(`/milestones/${id}`);

// ─────────────────────────────────────────────
// COST CATEGORIES
// ─────────────────────────────────────────────
export const listCategories = (rootOnly = false) => get(`/categories?root_only=${rootOnly}`);
export const createCategory = (data) => post('/categories', data);
export const updateCategory = (id, data) => put(`/categories/${id}`, data);
export const deleteCategory = (id) => del(`/categories/${id}`);

// ─────────────────────────────────────────────
// BOQ
// ─────────────────────────────────────────────
export const listBOQ = (pid, catId = '', skip = 0, limit = 500) => get(`/boq/project/${pid}?skip=${skip}&limit=${limit}${catId ? `&category_id=${catId}` : ''}`);
export const listProjectBOQs = (pid) => get(`/boq/project/${pid}/list`);
export const getBOQSummaryForProject = (pid) => get(`/boq/project/${pid}/summary`);
export const getBOQ = (id) => get(`/boq/${id}/full`);
export const createBOQ = (data) => post('/boq', data);
export const updateBOQ = (id, data) => put(`/boq/${id}`, data);
export const updateBOQStatus = (id, status) => put(`/boq/${id}/status`, { status });
export const deleteBOQ = (id) => del(`/boq/${id}`);
export const addBOQComponent = (id, data) => post(`/boq/${id}/components`, data);
export const updateBOQComponent = (id, data) => put(`/boq/components/${id}`, data);
export const deleteBOQComponent = (id) => del(`/boq/components/${id}`);
export const addBOQBreakdown = (id, data) => post(`/boq/${id}/breakdowns`, data);

// BOQ Budgets
export const listBOQBudgets = (pid) => get(`/projects/${pid}/boq-budgets`);
export const createBOQBudget = (pid, data) => post(`/projects/${pid}/boq-budgets`, data);
export const getBOQTotal = (pid) => get(`/projects/${pid}/boq-total`);
export const getBOQReconciliation = (pid) => get(`/projects/${pid}/boq-reconciliation`);

// ─────────────────────────────────────────────
// MATERIALS
// ─────────────────────────────────────────────
export const getMaterialDepartments = (materialId) => get(`/materials/${materialId}/departments`);
export const setMaterialDepartments = (materialId, departmentIds) =>
  put(`/materials/${materialId}/departments`, { department_ids: departmentIds });

export const listMaterials = (skip = 0, limit, q = '', departmentId = null) => {
  let url = `/materials?skip=${skip}`;
  if (limit != null) url += `&limit=${limit}`;
  if (q) url += `&q=${encodeURIComponent(q)}`;
  if (departmentId) url += `&department_id=${encodeURIComponent(departmentId)}`;
  return get(url);
};
export const searchMaterials = (q) => get(`/materials/search?q=${encodeURIComponent(q)}`);
export const getMaterial = (id) => get(`/materials/${id}`);
export const createMaterial = (data) => post('/materials', data);
export const updateMaterial = (id, data) => put(`/materials/${id}`, data);
export const deleteMaterial = (id) => del(`/materials/${id}`);
export const reactivateMaterial = (id) => patch(`/materials/${id}/reactivate`);
export const bulkImportMaterials = (items) => post('/materials/bulk-import', items);

// ─────────────────────────────────────────────
// SUPPLIERS
// ─────────────────────────────────────────────
export const listSuppliers = (activeOnly = false, skip = 0, limit) =>
  get(`/suppliers?active_only=${activeOnly}&skip=${skip}` + (limit != null ? `&limit=${limit}` : ''));
export const searchSuppliers = (q) => get(`/suppliers/search?q=${encodeURIComponent(q)}`);
export const getSupplier = (id) => get(`/suppliers/${id}`);
export const createSupplier = (data) => post('/suppliers', data);
export const updateSupplier = (id, data) => patch(`/suppliers/${id}`, data);
export const deleteSupplier = (id) => del(`/suppliers/${id}`);

// ─────────────────────────────────────────────
// PURCHASE REQUESTS
// ─────────────────────────────────────────────
export const listPurchaseRequestTypes = (activeOnly = false) => get(`/purchase-request-types/?active_only=${activeOnly}`);
export const getPurchaseRequestType = (id) => get(`/purchase-request-types/${id}`);
export const createPurchaseRequestType = (data) => post('/purchase-request-types', data);
export const updatePurchaseRequestType = (id, data) => put(`/purchase-request-types/${id}`, data);
export const deletePurchaseRequestType = (id) => del(`/purchase-request-types/${id}`);
export const listLedgers = (activeOnly = false) => get(`/ledgers/?active_only=${activeOnly}`);
export const getLedger = (id) => get(`/ledgers/${id}`);
export const createLedger = (data) => post('/ledgers', data);
export const updateLedger = (id, data) => put(`/ledgers/${id}`, data);
export const deleteLedger = (id) => del(`/ledgers/${id}`);
export const listCostCentres = (activeOnly = false) => get(`/cost-centres/?active_only=${activeOnly}`);
export const getCostCentre = (id) => get(`/cost-centres/${id}`);
export const createCostCentre = (data) => post('/cost-centres', data);
export const updateCostCentre = (id, data) => put(`/cost-centres/${id}`, data);
export const deleteCostCentre = (id) => del(`/cost-centres/${id}`);
export const listCostObjectTypes = (activeOnly = false) => get(`/cost-object-types/?active_only=${activeOnly}`);
export const createCostObjectType = (data) => post('/cost-object-types', data);
export const updateCostObjectType = (id, data) => put(`/cost-object-types/${id}`, data);
export const deleteCostObjectType = (id) => del(`/cost-object-types/${id}`);
export const listCategoryAssetTypes = (activeOnly = false, costObjectTypeId = null) => {
  let url = `/category-asset-types/?active_only=${activeOnly}`;
  if (costObjectTypeId) url += `&cost_object_type_id=${costObjectTypeId}`;
  return get(url);
};
export const createCategoryAssetType = (data) => post('/category-asset-types', data);
export const updateCategoryAssetType = (id, data) => put(`/category-asset-types/${id}`, data);
export const deleteCategoryAssetType = (id) => del(`/category-asset-types/${id}`);
export const listCostObjects = (activeOnly = false, categoryAssetTypeId = null) => {
  let url = `/cost-objects/?active_only=${activeOnly}`;
  if (categoryAssetTypeId) url += `&category_asset_type_id=${categoryAssetTypeId}`;
  return get(url);
};
export const createCostObject = (data) => post('/cost-objects', data);
export const updateCostObject = (id, data) => put(`/cost-objects/${id}`, data);
export const deleteCostObject = (id) => del(`/cost-objects/${id}`);

export const generatePRRef = () => get('/purchase-requests/generate-ref');
export const listPRs = (pid) => get(`/purchase-requests/project/${pid}`);
// Recall a rejected request back into the queue that rejected it. Rejection is
// otherwise terminal, so this is the only route back from a mis-click.
export const reverseRejection = (instanceId) =>
  post(`/workflows/instances/${instanceId}/reverse-rejection`);

// Recall of an APPROVED requisition. Two calls rather than one, because this
// recall can destroy payment vouchers: the preview says exactly what would go,
// and nothing is deleted until the second call.
export const previewRecallApproved = (prId) =>
  get(`/purchase-requests/${prId}/recall/preview`);
export const recallApproved = (prId) =>
  post(`/purchase-requests/${prId}/recall`);

// Direct edit of a finished requisition — its own endpoint, not the
// requester's PUT: that one refuses to write the review columns this screen
// exists to correct.
export const directEditPR = (prId, payload) =>
  put(`/purchase-requests/${prId}/direct-edit`, payload);

export const listAllPRs = (skip = 0, limit = 2000, search = '') => {
  const q = new URLSearchParams({ skip, limit });
  if (search) q.set('search', search);
  return get(`/purchase-requests?${q.toString()}`);
};
export const getPurchaseHistoryDepartments = () => get(`/purchase-requests/purchase-history/departments`);
export const getPurchaseHistoryItems = (departmentId, skip = 0, limit = 200) => get(`/purchase-requests/purchase-history/departments/${departmentId}/items?skip=${skip}&limit=${limit}`);
export const listMyPRs = (skip = 0, limit = 50, search = '') => {
  const q = new URLSearchParams({ skip, limit });
  if (search) q.set('search', search);
  return get(`/purchase-requests/my-requests?${q.toString()}`);
};
export const getPrsByQueue = (stepName, skip = 0, limit = 50, search = '') => {
  const q = new URLSearchParams({ skip, limit });
  if (search) q.set('search', search);
  return get(`/purchase-requests/queue/${encodeURIComponent(stepName)}?${q.toString()}`);
};
// Cost Control 'Pending Consideration' — requisitions grouped by why they are
// parked. Its own endpoint because the panel needs per-group counts and totals,
// not a flat list.
export const getPendingConsideration = () => get('/purchase-requests/queue/pending-consideration');
export const getPrsByQueueReviewed = (stepName, skip = 0, limit = 50) =>
  get(`/purchase-requests/queue/${encodeURIComponent(stepName)}/reviewed?skip=${skip}&limit=${limit}`);
export const getApprovedPRs = (skip = 0, limit = 50, search = '') => {
  const q = new URLSearchParams({ skip, limit });
  if (search) q.set('search', search);
  return get(`/purchase-requests/approved?${q.toString()}`);
};
export const getPrsRejectedReturnedByQueue = (stepName, outcome, skip = 0, limit = 50, search = '') => {
  const q = new URLSearchParams({ skip, limit });
  if (outcome) q.set('outcome', outcome);
  if (search) q.set('search', search);
  return get(`/purchase-requests/queue/${encodeURIComponent(stepName)}/rejected-returned?${q.toString()}`);
};
export const getPR = (id) => get(`/purchase-requests/${id}`);
export const createPR = (data) => post('/purchase-requests', data);
export const updatePR = (id, data) => put(`/purchase-requests/${id}`, data);
export const updatePRStatus = (id, data) => put(`/purchase-requests/${id}/status`, data);
export const deletePR = (id) => del(`/purchase-requests/${id}`);
export const updatePRItemsCCReview = (id, data) => put(`/purchase-requests/${id}/cc-item-reviews`, data);
export const updatePRItemsApprovalReview = (id, data) => put(`/purchase-requests/${id}/approval-item-reviews`, data);

// ─────────────────────────────────────────────
// PURCHASE ORDERS
// ─────────────────────────────────────────────
export const listPOs = (pid) => get(`/purchases/project/${pid}`);
export const getPO = (id) => get(`/purchases/${id}`);
export const createPO = (data) => post('/purchases', data);
export const updatePO = (id, data) => put(`/purchases/${id}`, data);

// ─────────────────────────────────────────────
// GOODS RECEIVED NOTES
// ─────────────────────────────────────────────
export const listGRNs = (pid) => get(`/goods-received/project/${pid}`);
export const getGRN = (id) => get(`/goods-received/${id}`);
export const createGRN = (data) => post('/goods-received', data);
export const confirmGRN = (id) => post(`/goods-received/${id}/confirm`);

// ─────────────────────────────────────────────
// LABOUR
// ─────────────────────────────────────────────
export const listLabourEntries = (pid) => get(`/labour/entries/project/${pid}`);
export const getLabourEntry = (id) => get(`/labour/entries/${id}`);
export const createLabourEntry = (data) => post('/labour/entries', data);
export const deleteLabourEntry = (id) => del(`/labour/entries/${id}`);
export const getLabourSummary = (pid) => get(`/labour/summary/project/${pid}`);
export const listLabourBudgets = (pid) => get(`/labour/budgets/project/${pid}`);
export const createLabourBudget = (data) => post('/labour/budgets', data);
export const compareLabour = (pid) => get(`/labour/compare/project/${pid}`);

// ─────────────────────────────────────────────
// MATERIAL TRANSACTIONS
// ─────────────────────────────────────────────
export const listMaterialTxns = (pid) => get(`/material-transactions/project/${pid}?limit=200`);
export const listAllMaterialTxns = (pid) => get(`/material-transactions/all?limit=1000${pid ? `&project_id=${pid}` : ''}`);
export const createMaterialTxn = (data) => post('/material-transactions', data);
export const updateMaterialTxn = (id, data) => put(`/material-transactions/${id}`, data);
export const deleteMaterialTxn = (id) => del(`/material-transactions/${id}`);
export const batchSubmitMaterialTxns = (data) => patch('/material-transactions/batch-submit', data);

// ─────────────────────────────────────────────
// DIRECT MATERIAL REGISTER (DMR)
// ─────────────────────────────────────────────
export const listDmrCategories = () => get(`/dmr/categories`);
export const createDmrCategory = (data) => post(`/dmr/categories`, data);
export const updateDmrCategory = (id, data) => put(`/dmr/categories/${id}`, data);
export const deleteDmrCategory = (id) => del(`/dmr/categories/${id}`);
export const listDmrSubsections = (projectId) => get(`/dmr/subsections/project/${projectId}`);
export const createDmrSubsection = (data) => post(`/dmr/subsections`, data);
export const updateDmrSubsection = (id, data) => put(`/dmr/subsections/${id}`, data);
export const deleteDmrSubsection = (id) => del(`/dmr/subsections/${id}`);

// ─────────────────────────────────────────────
// VARIATIONS
// ─────────────────────────────────────────────
export const listVariations = (pid) => get(`/variations/project/${pid}`);
export const getVariation = (id) => get(`/variations/${id}`);
export const createVariation = (data) => post('/variations', data);
export const approveRejectVariation = (id, data) => patch(`/variations/${id}/approve_or_reject`, data);

// ─────────────────────────────────────────────
// OTHER COSTS
// ─────────────────────────────────────────────
export const listOtherCosts = (pid) => get(`/other-costs/project/${pid}`);
export const createOtherCost = (data) => post('/other-costs', data);
export const deleteOtherCost = (id) => del(`/other-costs/${id}`);

// ─────────────────────────────────────────────
// EXPENSES
// ─────────────────────────────────────────────
export const listExpenses = (pid) => get(`/expenses/project/${pid}`);
export const createExpense = (data) => post('/expenses', data);
export const updateExpenseStatus = (id, data) => patch(`/expenses/${id}/status`, data);

// ─────────────────────────────────────────────
// SITE ISSUES
// ─────────────────────────────────────────────
export const listSiteIssues = (pid) => get(`/site-issues/project/${pid}`);
export const getSiteIssue = (id) => get(`/site-issues/${id}`);
export const createSiteIssue = (data) => post('/site-issues', data);
export const updateSiteIssue = (id, data) => put(`/site-issues/${id}`, data);
export const deleteSiteIssue = (id) => del(`/site-issues/${id}`);

// ─────────────────────────────────────────────
// QS MEASUREMENTS
// ─────────────────────────────────────────────
export const listQSMeasurements = (pid) => get(`/qs-measurements/project/${pid}`);
export const createQSMeasurement = (data) => post('/qs-measurements', data);
export const updateQSMeasurement = (id, data) => put(`/qs-measurements/${id}`, data);
export const certifyQSMeasurement = (id) => post(`/qs-measurements/${id}/certify`);

// ─────────────────────────────────────────────
// ANALYTICS
// ─────────────────────────────────────────────
export const getCostSummary = (pid) => get(`/analytics/project/${pid}/cost-summary`);
export const getExecutiveKpis = () => get('/analytics/executive-kpis');
export const getExecutiveSummary = (skip = 0, limit = 10) => get(`/analytics/executive-summary?skip=${skip}&limit=${limit}`);
export const getLabourAnalytics = (pid) => get(`/analytics/project/${pid}/labour-summary`);
export const getAnalyticalSummary = (pid) => get(`/analytics/project/${pid}/analytical-summary`);
export const getProcurementPlan = (pid) => get(`/analytics/project/${pid}/procurement-plan`);
export const getAlerts = (pid) => get(`/analytics/project/${pid}/alerts`);
export const getAlertCount = (pid) => get(`/analytics/project/${pid}/alerts/count`);
export const markAlertRead = (id) => patch(`/analytics/alerts/${id}/read`);
export const resolveAlert = (id) => patch(`/analytics/alerts/${id}/resolve`);

// ─────────────────────────────────────────────
// NOTIFICATIONS (the envelope)
// ─────────────────────────────────────────────
// Deliberately separate from the alerts above: those watch projects for budget
// and schedule trouble, these tell one person that something of theirs moved.
// Two feeds, two badges, no shared state.
export const listNotifications = (params = {}) => {
  const q = new URLSearchParams();
  if (params.skip != null) q.set('skip', params.skip);
  if (params.limit != null) q.set('limit', params.limit);
  if (params.unreadOnly) q.set('unread_only', 'true');
  const qs = q.toString();
  return get(`/notifications/${qs ? `?${qs}` : ''}`);
};
export const getNotificationCount = () => get('/notifications/count');
export const markNotificationsRead = (ids) =>
  patch('/notifications/read', { notification_ids: ids });
export const markAllNotificationsRead = () => patch('/notifications/read-all');
// Removes only rows already marked read — an unread notification can never
// be lost to a mistimed click.
export const clearReadNotifications = () => del('/notifications/read');
// Per-category unread counts for the Returned/Rejected inbox badges.
export const getInboxCategoryCounts = () => get('/notifications/inbox-counts');
// Mark all returned/rejected notifications for one entity as read.
export const markEntitySeen = (entityId, entityType = 'purchase_request') =>
  post('/notifications/mark-entity-seen', { entity_id: entityId, entity_type: entityType });

// Web push. getPushConfig reports whether the server has VAPID keys at all,
// so the client can fall back instead of offering something that cannot work.
export const getPushConfig = () => get('/notifications/push/config');
export const pushSubscribe = (subscription) => post('/notifications/push/subscribe', subscription);
export const pushUnsubscribe = (endpoint) => post('/notifications/push/unsubscribe', { endpoint });

// ─────────────────────────────────────────────
// ─────────────────────────────────────────────
// MASTER DATA (Suppliers, Ledgers, Expenses, Budgets)
// ─────────────────────────────────────────────
// Account Ledgers
export const listAccountLedgers = () => get('/account-ledgers');
export const getAccountLedger = (id) => get(`/account-ledgers/${id}`);
export const createAccountLedger = (data) => post('/account-ledgers', data);
export const updateAccountLedger = (id, data) => put(`/account-ledgers/${id}`, data);
export const deleteAccountLedger = (id) => del(`/account-ledgers/${id}`);

// Budget Control Levels
export const listBudgetControlLevels = () => get('/budget-control-levels');
export const getBudgetControlLevel = (id) => get(`/budget-control-levels/${id}`);
export const createBudgetControlLevel = (data) => post('/budget-control-levels', data);
export const updateBudgetControlLevel = (id, data) => put(`/budget-control-levels/${id}`, data);
export const deleteBudgetControlLevel = (id) => del(`/budget-control-levels/${id}`);

// Cost Groups
export const listCostGroups = () => get('/cost-groups');
export const getCostGroup = (id) => get(`/cost-groups/${id}`);
export const createCostGroup = (data) => post('/cost-groups', data);
export const updateCostGroup = (id, data) => put(`/cost-groups/${id}`, data);
export const deleteCostGroup = (id) => del(`/cost-groups/${id}`);

// Expense Categories
export const listExpenseCategories = () => get('/expense-categories');
export const getExpenseCategory = (id) => get(`/expense-categories/${id}`);
export const createExpenseCategory = (data) => post('/expense-categories', data);
export const updateExpenseCategory = (id, data) => put(`/expense-categories/${id}`, data);
export const deleteExpenseCategory = (id) => del(`/expense-categories/${id}`);

// Expense sub categories — the narrower headings beneath a category.
// listExpenseSubCategories() with no argument returns every one; the
// requisition grid loads them all once and groups by parent, rather than
// firing a request per line as categories are picked.
export const listExpenseSubCategories = (expenseCategoryId) =>
  get(`/expense-sub-categories${expenseCategoryId ? `?expense_category_id=${expenseCategoryId}` : ''}`);
export const createExpenseSubCategory = (data) => post('/expense-sub-categories', data);
export const updateExpenseSubCategory = (id, data) => put(`/expense-sub-categories/${id}`, data);
export const deleteExpenseSubCategory = (id) => del(`/expense-sub-categories/${id}`);

// ─────────────────────────────────────────────
// DAILY INSTALLED QUANTITIES (QS INSTALLATION)
// ─────────────────────────────────────────────
export const listDailyInstalled = (pid, skip = 0, limit = 100) => get(`/daily-installed/project/${pid}?skip=${skip}&limit=${limit}`);
export const getDailyInstalledSummary = (pid) => get(`/daily-installed/project/${pid}/summary`);
export const createDailyInstalled = (data) => post(`/daily-installed`, data);
export const createBulkDailyInstalled = (data) => post(`/daily-installed/bulk`, data);
export const updateDailyInstalled = (id, data) => put(`/daily-installed/${id}`, data);
export const deleteDailyInstalled = (id) => del(`/daily-installed/${id}`);

export const addBOQItem = (boqId, data) => post(`/boq/${boqId}/item`, data);
export const updateBOQItem = (itemId, data) => put(`/boq/item/${itemId}`, data);
export const deleteBOQItem = (itemId) => del(`/boq/item/${itemId}`);

// ─────────────────────────────────────────────
// ─────────────────────────────────────────────
// BOQ Navigation (Project -> Package -> Revision -> Unit)
// ─────────────────────────────────────────────
export const getPackageTypes = () => get('/boq/package-types');
export const createPackageType = (data) => post('/boq/package-types', data);
export const updatePackageType = (id, data) => put(`/boq/package-types/${id}`, data);
export const deletePackageType = (id) => del(`/boq/package-types/${id}`);
export const listProjectPackages = (projectId) => get(`/boq/boq-packages/project/${projectId}`);
export const createProjectPackage = (projectId, payload) => post(`/boq/boq-packages/project/${projectId}`, payload);
export const updateProjectPackage = (packageId, payload) => put(`/boq/boq-packages/${packageId}`, payload);
export const deleteProjectPackage = (packageId) => del(`/boq/boq-packages/${packageId}`);

export const listPackageRevisions = (packageId) => get(`/boq/boq-revisions/package/${packageId}`);
// BOQ workbook importer: upload → preview (server reads the .xlsx, nothing imported) → confirm (one transaction)
export const previewBoqImport = (projectId, file) => {
  const fd = new FormData();
  fd.append('file', file);
  return upload('POST', `/boq/imports/project/${projectId}`, fd);
};
export const listBoqImports = (projectId) => get(`/boq/imports/project/${projectId}`);
// an empty workbook in the importer's layout, one sheet per building/zone of the project
export const downloadBoqTemplate = (projectId) => blob(`/boq/imports/template${projectId ? `?project_id=${projectId}` : ''}`);
export const getBoqImport = (importId) => get(`/boq/imports/${importId}`);
export const confirmBoqImport = (importId, payload) => post(`/boq/imports/${importId}/confirm`, payload);
export const createPackageRevision = (packageId, payload) => post(`/boq/boq-revisions/package/${packageId}`, payload);
export const updatePackageRevision = (revisionId, payload) => put(`/boq/boq-revisions/${revisionId}`, payload);
export const deletePackageRevision = (revisionId) => del(`/boq/boq-revisions/${revisionId}`);

export const listRevisionUnits = (revisionId) => get(`/boq/boq-units/revision/${revisionId}`);
// BOQ Hub: a revision's BOQs, and a BOQ's building/zone tabs
export const listRevisionBOQs = (revisionId) => get(`/boq/boq-revisions/${revisionId}/boqs`);
export const addBOQBuilding = (boqId, projectDivisionId) => post(`/boq/${boqId}/buildings`, { project_division_id: projectDivisionId });
export const removeBOQBuilding = (boqId, projectDivisionId) => del(`/boq/${boqId}/buildings/${projectDivisionId}`);
export const createRevisionUnit = (revisionId, payload) => post(`/boq/boq-units/revision/${revisionId}`, payload);
export const updateRevisionUnit = (unitId, payload) => put(`/boq/boq-units/${unitId}`, payload);
export const deleteRevisionUnit = (unitId) => del(`/boq/boq-units/${unitId}`);
export const listUnitBOQs = (unitId) => get(`/boq/boq-units/${unitId}/boqs`);

// --- WORKFLOW CONFIGURATIONS ---
export const listWorkflowDefinitions = () => get('/workflow-configs/definitions');
export const getWorkflowDefinition = (id) => get(`/workflow-configs/definitions/${id}`);
export const createWorkflowDefinition = (data) => post('/workflow-configs/definitions', data);
export const updateWorkflowDefinition = (id, data) => put(`/workflow-configs/definitions/${id}`, data);
export const deleteWorkflowDefinition = (id) => del(`/workflow-configs/definitions/${id}`);
export const createWorkflowStep = (defId, data) => post(`/workflow-configs/definitions/${defId}/steps`, data);
export const updateWorkflowStep = (stepId, data) => put(`/workflow-configs/steps/${stepId}`, data);
export const deleteWorkflowStep = (stepId) => del(`/workflow-configs/steps/${stepId}`);
export const addRoleToStep = (stepId, data) => post(`/workflow-configs/steps/${stepId}/roles`, data);
export const removeRoleFromStep = (stepId, roleId) => del(`/workflow-configs/steps/${stepId}/roles/${roleId}`);

// Workflow & Approvals
export const getPRDashboardStats = () => get('/purchase-requests/dashboard-stats');
export const submitWorkflow = (payload) => post('/workflows/submit', payload);
export const getEligibleApprovers = (definitionCode, contextData = {}) => post(`/workflows/definitions/${definitionCode}/eligible-approvers`, contextData);
export const getMyInbox = () => get('/workflows/my-inbox');
export const getDepartmentInbox = (departmentId, stepName) => {
  const params = new URLSearchParams({ department_id: departmentId });
  if (stepName) params.set('step_name', stepName);
  return get(`/workflows/department-inbox?${params.toString()}`);
};
export const actOnWorkflow = (instanceId, payload) => post(`/workflows/instances/${instanceId}/act`, payload);
export const resubmitWorkflow = (instanceId, comment) => post(`/workflows/instances/${instanceId}/resubmit`, { comment: comment || null });

export const getWorkflowTracking = (instanceId) => get(`/workflows/instances/${instanceId}/tracking`);
export const getNextStepDetails = (instanceId) => get(`/workflows/instances/${instanceId}/next-step-details`);
// Where a return may be sent: the requester, or any earlier step this request
// has already passed through. The act endpoint validates against the same
// rule, so the dropdown cannot offer something the server would refuse.
export const getReturnTargets = (instanceId) => get(`/workflows/instances/${instanceId}/return-targets`);

// ─────────────────────────────────────────────
// PAYMENT VOUCHER ENGINE
// Its own module — approve/reject/return/hold, resubmit, recall, and cancel
// all go through the generic workflow endpoints above
// (actOnWorkflow/resubmitWorkflow/getWorkflowTracking) using the voucher's
// own workflow_instance_id, same as purchase requests.
// ─────────────────────────────────────────────
export const listApprovedRequisitionsForVoucher = (params = {}) => {
  const q = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '') q.set(k, v); });
  const qs = q.toString();
  return get(`/payment-vouchers/approved-requisitions${qs ? `?${qs}` : ''}`);
};
export const getRequisitionForVoucherLoad = (purchaseRequestId) => get(`/payment-vouchers/approved-requisitions/${purchaseRequestId}`);
// state: 'on_hold' | 'suspended' to freeze payment, null to release it.
// Does not change the requisition's approval status — only its disbursement.
// Correct a voucher still awaiting review. Versions it and records every
// changed field — deliberately separate from updatePaymentVoucher, which
// only edits drafts.
export const reviewEditVoucher = (voucherId, payload) =>
  put(`/payment-vouchers/${voucherId}/review-edit`, payload);

export const setRequisitionPaymentHold = (purchaseRequestId, { state, reason }) =>
  post(`/payment-vouchers/approved-requisitions/${purchaseRequestId}/payment-hold`, { state: state || null, reason: reason || null });

export const markRequisitionFullyProcessed = (purchaseRequestId) =>
  post(`/payment-vouchers/approved-requisitions/${purchaseRequestId}/mark-fully-processed`, {});

export const getNextVoucherNo = () => get('/payment-vouchers/next-number');
export const getPaymentVoucherDashboardStats = () => get('/payment-vouchers/dashboard-stats');
export const createPaymentVoucher = (payload) => post('/payment-vouchers', payload);
export const updatePaymentVoucher = (voucherId, payload) => put(`/payment-vouchers/${voucherId}`, payload);
export const deletePaymentVoucher = (voucherId) => del(`/payment-vouchers/${voucherId}`);
export const getPaymentVoucher = (voucherId) => get(`/payment-vouchers/${voucherId}`);
export const listPaymentVouchers = (params = {}) => {
  const q = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '') q.set(k, v); });
  const qs = q.toString();
  return get(`/payment-vouchers${qs ? `?${qs}` : ''}`);
};
export const submitPaymentVoucher = (voucherId, payload = {}) => post(`/payment-vouchers/${voucherId}/submit`, payload);
// ── Voucher Review & Release ────────────────────────────────
// Rows, header tiles and tab counts in one call — the tiles are counted from
// the same rows the table renders, so they can never disagree.
export const getVoucherReviewQueue = (skip = 0, limit = 10) => get(`/payment-vouchers/review-release?skip=${skip}&limit=${limit}`);
// Roles configured on the final workflow step; the screen hides its action
// buttons for anyone not holding one.
export const getVoucherReviewAllowedRoles = () => get('/payment-vouchers/review-release/allowed-roles');

// Releasing replaces the old /post step: posting now happens at submission,
// so the final act on a voucher is releasing it into the Expense Register.
export const releasePaymentVoucher = (voucherId, payload = {}) => post(`/payment-vouchers/${voucherId}/release`, payload);
// on_hold: false lifts an existing hold. A reason is required to place one.
export const holdPaymentVoucher = (voucherId, { on_hold = true, reason = null } = {}) =>
  post(`/payment-vouchers/${voucherId}/hold`, { on_hold, reason });
export const getFinanceHeadVoucherQueue = (skip = 0, limit = 50) => get(`/payment-vouchers/queue/finance-head?skip=${skip}&limit=${limit}`);
export const getApprovalVoucherQueue = (skip = 0, limit = 50) => get(`/payment-vouchers/queue/approval?skip=${skip}&limit=${limit}`);
export const getPostedVoucherRegister = (skip = 0, limit = 200, search = '') => get(`/payment-vouchers/register/posted?skip=${skip}&limit=${limit}${search ? `&search=${encodeURIComponent(search)}` : ''}`);
export const getPostedVoucherRowDetail = (voucherId) => get(`/payment-vouchers/register/posted/${voucherId}/detail`);

// Payment Voucher Methods (master data, managed from Administration)
// ─────────────────────────────────────────────
// ALL-IN-ONE EXPENSE REGISTER
// Single GET assembling every approved/posted voucher line with its
// requisition context — all grouping/filtering happens client-side.
// ─────────────────────────────────────────────
export const getExpenseRegister = () => get('/expense-register');

export const listPaymentVoucherMethods = (activeOnly = false) => get(`/payment-voucher-methods/?active_only=${activeOnly}`);
export const getPaymentVoucherMethod = (id) => get(`/payment-voucher-methods/${id}`);
export const createPaymentVoucherMethod = (data) => post('/payment-voucher-methods/', data);
export const updatePaymentVoucherMethod = (id, data) => put(`/payment-voucher-methods/${id}`, data);
export const deletePaymentVoucherMethod = (id) => del(`/payment-voucher-methods/${id}`);

// ─────────────────────────────────────────────
// MATERIAL REQUEST
// ─────────────────────────────────────────────
export const getMRSummary = (projectId = null) =>
  get(`/material-requests/summary${projectId ? `?project_id=${projectId}` : ''}`);

export const listMaterialRequests = (params = {}) => {
  const q = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => { if (v != null && v !== '') q.append(k, v); });
  const qs = q.toString();
  return get(`/material-requests${qs ? `?${qs}` : ''}`);
};

export const listAllMRs = (skip = 0, limit = 26, search = '') => {
  const q = new URLSearchParams({ skip, limit });
  if (search) q.set('search', search);
  return get(`/material-requests?${q.toString()}`);
};

export const getMaterialRequest = (id) => get(`/material-requests/${id}`);
export const createMaterialRequest = (payload, projectCode) =>
  post(`/material-requests?project_code=${encodeURIComponent(projectCode)}`, payload);
export const updateMaterialRequest = (id, payload) => put(`/material-requests/${id}`, payload);
export const deleteMaterialRequest = (id) => del(`/material-requests/${id}`);

export const submitMaterialRequest = (id, payload = {}) => post(`/material-requests/${id}/submit`, payload);
export const startMRCCReview = (id) => post(`/material-requests/${id}/cc-review/start`, {});
export const completeMRCCReview = (id, payload = {}) => post(`/material-requests/${id}/cc-review/complete`, payload);
export const completeMRStoreReview = (id, payload = {}) => post(`/material-requests/${id}/store-review/complete`, payload);
export const completeMRMgmtApproval = (id, payload = {}) => post(`/material-requests/${id}/mgmt-approval/complete`, payload);
export const recallMR = (id) => post(`/material-requests/${id}/recall`, {});

export const addMRCategory = (requestId, payload) => post(`/material-requests/${requestId}/categories`, payload);
export const updateMRCategory = (catId, payload) => put(`/material-requests/categories/${catId}`, payload);
export const deleteMRCategory = (catId) => del(`/material-requests/categories/${catId}`);

export const addMRLineItem = (catId, payload) => post(`/material-requests/categories/${catId}/line-items`, payload);
export const bulkAddMRLineItems = (catId, items) => post(`/material-requests/categories/${catId}/line-items/bulk`, items);
export const updateMRLineItem = (itemId, payload) => put(`/material-requests/line-items/${itemId}`, payload);
export const deleteMRLineItem = (itemId) => del(`/material-requests/line-items/${itemId}`);

export const mrCCReviewLine = (itemId, payload) => patch(`/material-requests/line-items/${itemId}/cc-review`, payload);
export const mrStoreReviewLine = (itemId, payload) => patch(`/material-requests/line-items/${itemId}/store-review`, payload);
export const mrMgmtApproveLine = (itemId, payload) => patch(`/material-requests/line-items/${itemId}/mgmt-approval`, payload);

export const getMRCCStats = (projectId = null) => {
  const qs = projectId ? `?project_id=${projectId}` : '';
  return get(`/material-requests/cc-stats${qs}`);
};

export const getMRCCProjects = (statuses = []) => {
  const qs = statuses.length ? `?statuses=${statuses.join(',')}` : '';
  return get(`/material-requests/cc-projects${qs}`);
};

export const getMRCCQueue = (params = {}) => {
  const q = new URLSearchParams(Object.fromEntries(Object.entries(params).filter(([, v]) => v != null && v !== '')));
  return get(`/material-requests/queue/cc-review${q.toString() ? `?${q}` : ''}`);
};
export const getMRStoreStats = (projectId = null) => {
  const qs = projectId ? `?project_id=${projectId}` : '';
  return get(`/material-requests/store-stats${qs}`);
};

export const getMRStoreQueue = (params = {}) => {
  const q = new URLSearchParams(Object.fromEntries(Object.entries(params).filter(([, v]) => v != null && v !== '')));
  return get(`/material-requests/queue/store-review${q.toString() ? `?${q}` : ''}`);
};
export const getMRMgmtStats = (projectId = null) => {
  const qs = projectId ? `?project_id=${projectId}` : '';
  return get(`/material-requests/mgmt-stats${qs}`);
};

export const getMRMgmtQueue = (params = {}) => {
  const q = new URLSearchParams(Object.fromEntries(Object.entries(params).filter(([, v]) => v != null && v !== '')));
  return get(`/material-requests/queue/mgmt-approval${q.toString() ? `?${q}` : ''}`);
};

export const getMRLifecycleStats = (projectId = null) => {
  const qs = projectId ? `?project_id=${projectId}` : '';
  return get(`/material-requests/lifecycle-stats${qs}`);
};

// ─────────────────────────────────────────────
// MATERIAL PURCHASE ORDERS
// ─────────────────────────────────────────────

export const getMPOProjectSummary = (projectIds = null) => {
  const qs = projectIds && projectIds.length
    ? `?project_ids=${projectIds.join(',')}`
    : '';
  return get(`/material-purchase-orders/project-summary${qs}`);
};

export const listMPOs = (params = {}) => {
  const q = new URLSearchParams(
    Object.fromEntries(Object.entries(params).filter(([, v]) => v != null && v !== ''))
  );
  return get(`/material-purchase-orders/${q.toString() ? `?${q}` : ''}`);
};

export const getMPO = (id) => get(`/material-purchase-orders/${id}`);

export const createMPO = (payload) => post('/material-purchase-orders/', payload);

export const updateMPO = (id, payload) => patch(`/material-purchase-orders/${id}`, payload);

export const deleteMPO = (id) => del(`/material-purchase-orders/${id}`);

export const submitMPO = (id, payload = {}) =>
  post(`/material-purchase-orders/${id}/submit`, payload);

export const approveMPO = (id, payload = {}) =>
  post(`/material-purchase-orders/${id}/approve`, payload);

export const returnMPOToDraft = (id, payload = {}) =>
  post(`/material-purchase-orders/${id}/return-to-draft`, payload);

export const rejectMPO = (id, payload = {}) =>
  post(`/material-purchase-orders/${id}/reject`, payload);

export const issueMPO = (id, payload = {}) =>
  post(`/material-purchase-orders/${id}/issue`, payload);

export const cancelMPO = (id, payload = {}) =>
  post(`/material-purchase-orders/${id}/cancel`, payload);

export const closeMPO = (id, payload = {}) =>
  post(`/material-purchase-orders/${id}/close`, payload);

// Review queues: Cost Control's workspaces (load | reviewed | rejected, plus the grouped Pending Consideration)
// and Management Approval ('management'). Actions go through actOnWorkflow, as on requisitions.
export const getMPOReviewCounts = () => get('/material-purchase-orders/review-queue/counts');
export const getMPOReviewQueue = (view, params = {}) => {
  const q = new URLSearchParams(
    Object.fromEntries(Object.entries(params).filter(([, v]) => v != null && v !== ''))
  );
  return get(`/material-purchase-orders/review-queue/${view}${q.toString() ? `?${q}` : ''}`);
};
export const getMPOPendingConsideration = () => get('/material-purchase-orders/review-queue/pending-consideration');
export const saveMPOCCReview = (id, data) => put(`/material-purchase-orders/${id}/cc-item-reviews`, data);
export const saveMPOApprovalReview = (id, data) => put(`/material-purchase-orders/${id}/approval-item-reviews`, data);

// ─────────────────────────────────────────────
// MATERIAL TAKE-OFF  (approvals run in the workflow engine — definition MATERIAL_TAKEOFF)
// ─────────────────────────────────────────────
const mtoQuery = (params = {}) => {
  const q = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => { if (v != null && v !== '' && v !== false) q.append(k, v); });
  const s = q.toString();
  return s ? `?${s}` : '';
};
export const getTakeoffSummary = (projectId) => get(`/material-takeoffs/summary${mtoQuery({ project_id: projectId })}`);
// a page of take-offs; the full count is on the array as `totalCount`
export const listTakeoffs = (params = {}) => get(`/material-takeoffs${mtoQuery(params)}`);
// Approved take-offs with BOQ lines still to be requested (Material Request → Load Approved Take-Offs).
export const listTakeoffsAwaitingMR = (params = {}) => get(`/material-takeoffs/awaiting-material-request${mtoQuery(params)}`);
export const getTakeoffBoqOptions = (projectId, divisionId) =>
  get(`/material-takeoffs/boq-options${mtoQuery({ project_id: projectId, project_division_id: divisionId })}`);
export const createTakeoff = (payload) => post('/material-takeoffs', payload);
export const getTakeoff = (id) => get(`/material-takeoffs/${id}`);
export const saveTakeoff = (id, payload) => put(`/material-takeoffs/${id}`, payload);
export const addTakeoffSystem = (id, payload) => post(`/material-takeoffs/${id}/systems`, payload);
export const removeTakeoffSystem = (id, systemId) => del(`/material-takeoffs/${id}/systems/${systemId}`);
export const deleteTakeoff = (id) => del(`/material-takeoffs/${id}`);
export const submitTakeoff = (id, comment) => post(`/material-takeoffs/${id}/submit`, { comment: comment || null });
export const approveTakeoff = (id, comment) => post(`/material-takeoffs/${id}/approve`, { comment: comment || null });
export const returnTakeoff = (id, comment) => post(`/material-takeoffs/${id}/return`, { comment });
export const rejectTakeoff = (id, comment) => post(`/material-takeoffs/${id}/reject`, { comment });
export const recallTakeoff = (id) => post(`/material-takeoffs/${id}/recall`, {});
export const reviseTakeoff = (id, reason) => post(`/material-takeoffs/${id}/revise`, { reason });
export const raiseMRFromTakeoff = (id, payload = {}) => post(`/material-takeoffs/${id}/material-request`, payload);

// ─────────────────────────────────────────────
// BOQ HUB → MATERIAL MAPPINGS (BOQ line ↔ stock material)
// ─────────────────────────────────────────────
const MM = '/boq-material-mapping';
export const getMappingSummary = (projectId) => get(`${MM}/summary${mtoQuery({ project_id: projectId })}`);
export const listMappingProjects = () => get(`${MM}/projects`);
// a page of current BOQ lines with their mapping; the full count is on the array as `totalCount`
export const listMappingRegister = (params = {}) => get(`${MM}/register${mtoQuery(params)}`);
export const getMappingForBoqLine = (boqItemId) => get(`${MM}/boq-line/${boqItemId}`);
export const getMappingSuggestions = (boqItemId, limit = 15) => get(`${MM}/boq-line/${boqItemId}/suggestions?limit=${limit}`);
export const getMaterialAccountability = (boqItemId) => get(`${MM}/boq-line/${boqItemId}/accountability`);
export const getMapping = (id) => get(`${MM}/mappings/${id}`);
export const saveMapping = (payload) => post(`${MM}/mappings`, payload);
export const updateMapping = (id, payload) => put(`${MM}/mappings/${id}`, payload);
export const deleteMapping = (id) => del(`${MM}/mappings/${id}`);
export const submitMapping = (id, comment) => post(`${MM}/mappings/${id}/submit`, { comment });
export const submitMappings = (ids) => post(`${MM}/mappings/submit-many`, { ids });
export const approveMapping = (id, comment) => post(`${MM}/mappings/${id}/approve`, { comment });
export const returnMapping = (id, comment) => post(`${MM}/mappings/${id}/return`, { comment });
export const rejectMapping = (id, comment) => post(`${MM}/mappings/${id}/reject`, { comment });
export const recallMapping = (id) => post(`${MM}/mappings/${id}/recall`, {});
export const reopenMapping = (id) => post(`${MM}/mappings/${id}/reopen`, {});
export const listUnmappedMaterials = (projectId, includeClassified = false) =>
  get(`${MM}/unmapped-materials${mtoQuery({ project_id: projectId, include_classified: includeClassified })}`);
export const setMaterialMappingException = (payload) => post(`${MM}/exceptions`, payload);
export const clearMaterialMappingException = (id) => del(`${MM}/exceptions/${id}`);
export const listMappedMaterials = (params = {}) => get(`${MM}/mapped-materials${mtoQuery(params)}`);
export const getMappedMaterialStats = (projectId) => get(`${MM}/mapped-materials/stats${mtoQuery({ project_id: projectId })}`);
export const listSiteMeasurement = (params = {}) => get(`${MM}/site-measurement${mtoQuery(params)}`);

// ─────────────────────────────────────────────
// BOQ HUB → CODE GENERATOR (PREFIX-DISC-0001, sub-codes …-01)
// ─────────────────────────────────────────────
const BC = '/boq-codes';
export const listCodeDisciplines = () => get(`${BC}/disciplines`);
export const addCodeDiscipline = (payload) => post(`${BC}/disciplines`, payload);
export const updateCodeDiscipline = (id, payload) => put(`${BC}/disciplines/${id}`, payload);
export const getCodeSettings = (projectId) => get(`${BC}/settings?project_id=${projectId}`);
export const setCodePrefix = (projectId, prefix) => put(`${BC}/settings?project_id=${projectId}`, { prefix });
export const getCodeSummary = (projectId) => get(`${BC}/summary?project_id=${projectId}`);
export const generateBoqCodes = (payload) => post(`${BC}/generate`, payload);
export const generateSubCodes = (codeId, payload) => post(`${BC}/${codeId}/sub-codes`, payload);
// a page of the register; the full count is on the array as `totalCount`
export const listBoqCodes = (params = {}) => get(`${BC}${mtoQuery(params)}`);
export const updateBoqCode = (id, payload) => put(`${BC}/${id}`, payload);
export const voidBoqCode = (id, reason) => post(`${BC}/${id}/void`, { reason });
export const restoreBoqCode = (id) => post(`${BC}/${id}/restore`, {});
export const previewApplyCodes = (payload) => post(`${BC}/apply/preview`, payload);
export const applyBoqCodes = (payload) => post(`${BC}/apply`, payload);
export const importBoqCodes = (payload) => post(`${BC}/import`, payload);
